import type {
  NotificationSchema,
  NotificationType,
} from "@/app/shared/services/volateq-api/api-schemas/notification-schema";

export interface NotificationGroup {
  key: string;
  analysisId: string | null;
  label: string;
  notifications: NotificationSchema[];
  unreadCount: number;
}

export interface Fh2ImportNotificationCounts {
  succeeded: number;
  failed: number;
}

/** `context` of an UPLOAD_COMPLETED notification whose data still needs a follow-up. */
const UPLOAD_DATA_INCOMPLETE_CONTEXT = "DATA_INCOMPLETE";

/** Context prefix for FH2 file-import completion notifications: `FH2_IMPORT:<succeeded>:<failed>`. */
const FH2_IMPORT_CONTEXT_PREFIX = "FH2_IMPORT:";

/** Why a role was assigned automatically (backend RoleAutoAssignReason). */
export type RoleAutoAssignReason = "NEW_ANALYSIS" | "OPTED_OUT" | "REMOVED_FROM_OPS_TEAM";

export interface RoleAssignedContext {
  role: string;
  autoAssignReason: RoleAutoAssignReason | null;
}

export class NotificationHelper {
  /** ROLE_ASSIGNED context is "<role>" or, when assigned automatically, "<role>:<reason>". */
  static parseRoleAssignedContext(notification: NotificationSchema): RoleAssignedContext {
    const [role, autoAssignReason] = (notification.context || "").split(":");
    return { role, autoAssignReason: (autoAssignReason as RoleAutoAssignReason) || null };
  }

  /** True while an upload finished but the uploaded data is not complete yet. */
  static isIncompleteUpload(notification: NotificationSchema): boolean {
    return (
      notification.notification_type === "UPLOAD_COMPLETED" &&
      notification.context === UPLOAD_DATA_INCOMPLETE_CONTEXT
    );
  }

  /**
   * Parses the namespaced FH2 import context. Returns null when the notification is not an
   * FH2 import completion (ordinary upload notifications keep their existing context values).
   */
  static parseFh2ImportCounts(notification: NotificationSchema): Fh2ImportNotificationCounts | null {
    const context = notification.context;
    if (!context || !context.startsWith(FH2_IMPORT_CONTEXT_PREFIX)) {
      return null;
    }

    const parts = context.split(":");
    if (parts.length !== 3) {
      return null;
    }

    const succeeded = Number(parts[1]);
    const failed = Number(parts[2]);
    if (!Number.isFinite(succeeded) || !Number.isFinite(failed)) {
      return null;
    }

    return { succeeded, failed };
  }

  /** True for a message from the evaluation pipeline, whose text is in `context`. */
  static isEvaluationMessage(notification: NotificationSchema): boolean {
    return (
      notification.notification_type === "EVALUATION_ERROR" ||
      notification.notification_type === "EVALUATION_INFO"
    );
  }

  /** The pipeline's message of an evaluation notification (may hold a stack trace), else null. */
  static evaluationMessage(notification: NotificationSchema): string | null {
    return NotificationHelper.isEvaluationMessage(notification) ? notification.context : null;
  }

  /** "EB3" for an evaluation message of batch 3, null when the pipeline sent no batch. */
  static evalBatchLabel(notification: NotificationSchema): string | null {
    return notification.eval_batch_num === null ? null : `EB${notification.eval_batch_num}`;
  }

  /** True when the notification tagged its recipient personally, rather than the whole analysis. */
  static isMention(notification: NotificationSchema): boolean {
    return notification.notification_type === "MENTION";
  }

  /**
   * Group keys in the order the given notifications produce them. Used to pin the group order
   * while the panel is open, so marking one notification read never moves a group under the cursor.
   */
  static groupKeys(notifications: NotificationSchema[]): string[] {
    const keys: string[] = [];

    for (const notification of notifications) {
      const key = NotificationHelper.groupKey(notification);
      if (!keys.includes(key)) {
        keys.push(key);
      }
    }

    return keys;
  }

  /**
   * Groups the notifications by analysis. `keyOrder` (see groupKeys) pins the resulting order;
   * groups missing from it keep their natural order behind the pinned ones.
   */
  static groupByAnalysis(
    notifications: NotificationSchema[],
    fallbackLabel: string,
    keyOrder: string[] = [],
  ): NotificationGroup[] {
    const groups = new Map<string, NotificationGroup>();

    for (const notification of notifications) {
      const key = NotificationHelper.groupKey(notification);
      let group = groups.get(key);

      if (!group) {
        group = {
          key,
          analysisId: notification.analysis_id,
          label: notification.analysis_name || fallbackLabel,
          notifications: [],
          unreadCount: 0,
        };
        groups.set(key, group);
      }

      group.notifications.push(notification);
      if (!notification.read) {
        group.unreadCount += 1;
      }
    }

    return Array.from(groups.values()).sort(
      (a, b) => NotificationHelper.keyOrderIndex(a.key, keyOrder) - NotificationHelper.keyOrderIndex(b.key, keyOrder),
    );
  }

  private static groupKey(notification: NotificationSchema): string {
    return notification.analysis_id || "general";
  }

  /** Unknown keys sort behind all pinned ones, keeping their natural order among each other. */
  private static keyOrderIndex(key: string, keyOrder: string[]): number {
    const index = keyOrder.indexOf(key);
    return index === -1 ? keyOrder.length : index;
  }

  static icon(notification: NotificationSchema): string {
    if (NotificationHelper.isIncompleteUpload(notification)) {
      return "cloud-arrow-up";
    }

    const icons: Record<NotificationType, string> = {
      MENTION: "at",
      ROLE_ASSIGNED: "person-check",
      READY_FOR_REVIEW: "clipboard-check",
      UPLOAD_COMPLETED: "cloud-check",
      UPLOAD_FAILED: "cloud-slash",
      NOTE_ADDED: "chat-left-text",
      REACTION: "emoji-smile",
      EVALUATION_ERROR: "exclamation-octagon",
      EVALUATION_INFO: "exclamation-triangle",
      WHATSAPP_MESSAGE: "chat-dots",
      READY_FOR_EVAL: "clock-history",
      DEADLINE_CHANGED: "calendar-event",
    };
    return icons[notification.notification_type];
  }

  static tone(notification: NotificationSchema): string {
    // An upload that finished with incomplete data is not a success — it needs a follow-up.
    if (NotificationHelper.isIncompleteUpload(notification)) {
      return "warning";
    }

    // A mention is addressed at the user personally, so it stands out from the analysis noise.
    if (NotificationHelper.isMention(notification)) {
      return "mention";
    }

    const notificationType = notification.notification_type;

    if (notificationType === "UPLOAD_FAILED" || notificationType === "EVALUATION_ERROR") {
      return "danger";
    }
    if (notificationType === "EVALUATION_INFO") {
      return "warning";
    }
    if (
      notificationType === "UPLOAD_COMPLETED" ||
      notificationType === "READY_FOR_REVIEW" ||
      notificationType === "READY_FOR_EVAL"
    ) {
      return "success";
    }
    return "primary";
  }
}
